import React from "react";
import {
  MapPin,
  Globe,
  Truck,
  Users,
  Shield,
  Clock,
  Star,
  Award,
  CreditCard,
  Headphones,
  CheckCircle,
  Zap,
  Banknote,
} from "lucide-react";
import RegionCard from "./coverage/RegionCard";
import TrustFactorCard from "./coverage/TrustFactorCard";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";

const CoverageSection = () => {
  const { language } = useLanguage();
  const labels = commonLabels(language);
  const sectionData = labels.menuPages?.sellCar?.coverageSection || {};
  const regionsData = sectionData.regions || [];
  const trustFactorsData = sectionData.trustFactors || [];

  const icons = ["🇸🇪", "🌲", "🇪🇺"];

  const regions = regionsData.map((region: any, index: number) => ({
    title: region.title || "",
    description: region.description || "",
    cities: region.cities || [],
    icon: icons[index] || icons[0],
  }));

  const trustIcons = [
    <div className="relative flex items-center justify-center">
      <Shield className="w-12 h-12 text-green-400" />
    </div>,
    <div className="relative">
      <Banknote className="w-8 h-8" />
      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
        <CheckCircle className="w-2 h-2 text-white" />
      </div>
    </div>,
    <div className="relative">
      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
        <Headphones className="w-5 h-5 text-white" />
      </div>
    </div>,
    <div className="relative">
      <Clock className="w-8 h-8" />
      <div className="absolute -bottom-1 -right-1 w-6 h-3 bg-orange-500 rounded-full flex items-center justify-center">
        <span className="text-white text-xs font-bold">⚡</span>
      </div>
    </div>,
  ];

  const colors = [
    "from-green-400 to-emerald-600",
    "from-blue-400 to-cyan-600",
    "from-purple-400 to-pink-600",
    "from-orange-400 to-red-600",
  ];

  const trustFactors = trustFactorsData.map((factor: any, index: number) => ({
    icon: trustIcons[index] || trustIcons[0],
    title: factor.title || "",
    description: factor.description || "",
    color: colors[index] || colors[0],
  }));

  return (
    <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {sectionData.title || "Från Sverige till Hela Europa"}
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {sectionData.description ||
              "Vår geografiska räckvidd säkerställer att din bil når rätt köpare."}
          </p>
        </div>

        {/* Regional Coverage */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {regions.map((region, index) => (
            <RegionCard key={index} region={region} index={index} />
          ))}
        </div>

        {/* Trust & Quality Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl"></div>
          <div className="absolute inset-0 bg-black/20 rounded-3xl"></div>
          <div className="relative p-12 text-white">
            <h3 className="text-3xl font-bold text-center mb-12">
              {sectionData.trustTitle || "Din Trygghet i Fokus"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {trustFactors.map((factor, index) => (
                <TrustFactorCard key={index} factor={factor} index={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoverageSection;
